// Time window for the Messages page (both Channels and Sent). Pure logic, no imports.

export type MessageWindow = "24h" | "7d" | "all";

export const MESSAGE_WINDOW_OPTIONS: ReadonlyArray<{ value: MessageWindow; label: string }> = [
  { value: "24h", label: "24 h" },
  { value: "7d", label: "7 d" },
  { value: "all", label: "All" },
];

export const DEFAULT_MESSAGE_WINDOW: MessageWindow = "24h";
export const MESSAGE_WINDOW_STORAGE_KEY = "amalgamesh:messageWindow";

const HOURS: Record<MessageWindow, number | null> = { "24h": 24, "7d": 168, all: null };

/** Anything that is not a known option (missing, old, hand-edited) falls back to the default. */
export function parseMessageWindow(v: unknown): MessageWindow {
  return v === "24h" || v === "7d" || v === "all" ? v : DEFAULT_MESSAGE_WINDOW;
}

/** Lower bound of the window as an ISO time, or null for "All". */
export function windowSince(w: MessageWindow, nowMs: number = Date.now()): string | null {
  const h = HOURS[w];
  return h === null ? null : new Date(nowMs - h * 3_600_000).toISOString();
}

/** Query-string piece for the list endpoints: "&since=..." or "" (always starts with "&"). */
export function sinceQuery(w: MessageWindow, nowMs: number = Date.now()): string {
  const s = windowSince(w, nowMs);
  return s === null ? "" : `&since=${encodeURIComponent(s)}`;
}

/** Is a message time inside the window? Unparseable times are kept (never hide data on a guess). */
export function inWindow(iso: string | null | undefined, w: MessageWindow, nowMs: number = Date.now()): boolean {
  const h = HOURS[w];
  if (h === null) return true;
  const t = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(t)) return true;
  return t >= nowMs - h * 3_600_000;
}

/** Keep the rows inside the window, newest first (by the given time field), dropping duplicate ids. */
export function windowRows<T>(
  rows: ReadonlyArray<T>,
  idOf: (r: T) => string,
  timeOf: (r: T) => string,
  w: MessageWindow,
  nowMs: number = Date.now(),
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const r of rows) {
    const id = idOf(r);
    if (seen.has(id) || !inWindow(timeOf(r), w, nowMs)) continue;
    seen.add(id);
    out.push(r);
  }
  return out.sort((a, b) => (timeOf(a) < timeOf(b) ? 1 : timeOf(a) > timeOf(b) ? -1 : 0));
}
